import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useMutation, useLazyQuery, useApolloClient } from '@apollo/client';
import { uploadVideo, waitForVideo } from '../../lib/media/uploadVideo';
import { format } from 'date-fns';
import {
  Upload,
  Link as LinkIcon,
  Type,
  Image as ImageIcon,
  Video,
  X,
  ImagePlus,
  Loader2,
  Sparkles,
  RefreshCw,
  Eye,
} from 'lucide-react';
import {
  GET_POSTS,
  CREATE_POST_MUTATION,
  UPDATE_POST_MUTATION,
  ADAPT_CONTENT_FOR_PLATFORMS,
} from '../../graphql';
import { PlatformIcon } from '../common/PlatformIcon';
import { SocialPost, Account, Platform, PostType } from '../../types';
import { GetSocialProfilesResponse } from '../../types/graphql';
import { ApolloError } from '@apollo/client';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { TopicsSection } from '../topics/TopicsSection';
import { Topic, MediaType } from '../../types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { SocialPostPreview } from './SocialPostPreview';

export interface SimplifiedPostFormData {
  message: string;
  postType: PostType | '';
  mediaUrl: string;
  linkUrl: string;
  scheduleTime: number | Date;
  isStory: boolean;
  notes?: string;
  isDraft?: boolean;
}

// Helper to convert from PostFormData to SimplifiedPostFormData
export const convertToSimplifiedFormData = (postFormData: {
  title: string;
  summary: string;
  postUrl: string;
  imageUrl: string;
  thoughts: string;
  postType: PostType | '';
  scheduleTime: number | Date;
  notes?: string;
  isDraft?: boolean;
  platform?: Platform | '';
}): SimplifiedPostFormData => {
  return {
    message:
      postFormData.postType === 'text'
        ? postFormData.thoughts
        : postFormData.summary || postFormData.title || '',
    postType: postFormData.postType,
    mediaUrl: postFormData.imageUrl || '',
    linkUrl: postFormData.postUrl || '',
    scheduleTime: postFormData.scheduleTime,
    isStory: postFormData.postType === 'story',
    notes: postFormData.notes,
    isDraft: postFormData.isDraft,
  };
};

// Helper to convert from SimplifiedPostFormData to PostFormData format for submission
export const convertFromSimplifiedFormData = (
  simplifiedData: SimplifiedPostFormData
): {
  title: string;
  summary: string;
  postUrl: string;
  imageUrl: string;
  thoughts: string;
  postType: PostType | '';
  scheduleTime: number | Date;
  notes?: string;
  isDraft?: boolean;
} => {
  return {
    title: simplifiedData.postType === 'text' ? simplifiedData.message : simplifiedData.message,
    summary: simplifiedData.message,
    postUrl: simplifiedData.linkUrl,
    imageUrl: simplifiedData.mediaUrl,
    thoughts: simplifiedData.postType === 'text' ? simplifiedData.message : '',
    postType: simplifiedData.isStory ? 'story' : simplifiedData.postType,
    scheduleTime: simplifiedData.scheduleTime,
    notes: simplifiedData.notes,
    isDraft: simplifiedData.isDraft,
  };
};

interface SimplifiedPostFormProps {
  selectedPlatforms: Platform | '';
  selectedProfiles: Set<string>;
  selectedAccount: Pick<Account, 'id'> | null;
  selectedPost: SocialPost | null;
  socialProfilesData?: GetSocialProfilesResponse;
  editMode: boolean;
  formData: SimplifiedPostFormData;
  selectedFormProfiles: Set<string>;
  setSelectedFormProfiles: (profiles: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
  setSelectedPost: (post: SocialPost | null) => void;
  setEditMode: (mode: boolean) => void;
  setFormData: (
    data: SimplifiedPostFormData | ((prev: SimplifiedPostFormData) => SimplifiedPostFormData)
  ) => void;
  onClose?: () => void;
  accountId?: string;
}

// What each platform can actually PUBLISH — NOT a marketing capability list.
//
// This mirrors the pipeline's own capability matrix,
// smagent-apiserver/src/reach/services/postFormats.js, which is derived from
// smagent-engine/src/runtime/publishPlanner.ts (which post_type lands in which
// publish job) plus what that job's engine does with the row. Offering a
// combination the planner cannot route does not fail loudly — it publishes the
// wrong kind of post. Keep the two files in step.
//
// 'image' is a legacy alias for 'photo'; availablePlatformsForPostType resolves
// it through the 'photo' entry, so it is not listed per platform.
const PLATFORM_CAPABILITIES: Record<string, PostType[]> = {
  // No Reels or Stories publisher exists for Facebook: nothing calls
  // /{page}/video_reels or the Stories endpoints, so a 'reel'/'story' row falls
  // into publish.facebook.post and goes out as a plain text post.
  facebook: ['text', 'link', 'photo', 'video'],
  // Instagram requires media on every post — the post job publishes only
  // post_type='photo' and skips everything else. Reels and Stories come out of
  // the video job, so both are video-only (no photo Stories).
  instagram: ['photo', 'video', 'reel', 'story'],
  threads: ['text', 'link', 'photo', 'video'], // Threads has no Reels or Stories
  x: ['text', 'link', 'photo', 'video'], // X has no Reels or Stories
  twitter: ['text', 'link', 'photo', 'video'],
  // 'reel' means "intended as a Short" — YouTube decides Shorts from the file
  // (vertical, <= 60s), not from the upload request, so it changes no API call.
  youtube: ['video', 'reel'],
  tiktok: ['video'], // every TikTok post goes to the feed
  // Neither platform has a job in publishPlanner.ts nor an entry in
  // postFormats.js; left as-is rather than silently disabling the composer for
  // them. Revisit when their publish path is settled.
  linkedin: ['text', 'link', 'photo', 'video'],
  bluesky: ['text', 'link', 'photo', 'video'],
};

// Auto-detect post type from input
const detectPostType = (mediaUrl: string, linkUrl: string, message: string): PostType | '' => {
  if (linkUrl && !mediaUrl) {
    return 'link';
  }
  if (mediaUrl) {
    // Check if it's a video URL
    const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv', '.flv'];
    const isVideo = videoExtensions.some((ext) => mediaUrl.toLowerCase().includes(ext));
    if (isVideo) {
      return 'video';
    }
    // Otherwise it's likely an image
    return 'photo';
  }
  if (message && !linkUrl && !mediaUrl) {
    return 'text';
  }
  return '';
};

interface MetadataResponse {
  success: boolean;
  data?: {
    title?: string;
    summary?: string;
    image?: string;
  };
  error?: string;
  message?: string;
}

export function SimplifiedPostForm({
  selectedPlatforms,
  selectedProfiles,
  selectedAccount,
  selectedPost,
  socialProfilesData,
  editMode,
  formData,
  selectedFormProfiles,
  setSelectedFormProfiles,
  setSelectedPost,
  setEditMode,
  setFormData,
  onClose,
  accountId,
}: SimplifiedPostFormProps) {
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [metadataLoading, setMetadataLoading] = useState<boolean>(false);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Video progress ("Uploading 42%", "Preparing video…") in place of "Uploading...".
  const [uploadNote, setUploadNote] = useState<string | null>(null);
  const apollo = useApolloClient();
  const [mediaFilters, setMediaFilters] = useState<MediaType[]>([
    'IMAGE',
    'VIDEO',
    'GALLERY',
    'PHOTO',
    'BOOKMARK',
  ]);
  const [showContentAdaptationModal, setShowContentAdaptationModal] = useState(false);
  const [contentAdaptationError, setContentAdaptationError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const hasInitializedProfiles = useRef<boolean>(false);
  const previousEditMode = useRef<boolean>(editMode);

  // Helper function to check if a URL is a video
  const isVideoUrl = (url: string | undefined): boolean => {
    if (!url) return false;
    const videoExtensions = [
      '.mp4',
      '.mov',
      '.avi',
      '.webm',
      '.mkv',
      '.flv',
      '.wmv',
      '.m4v',
      '.mpg',
      '.mpeg',
      '.3gp',
    ];
    const lowerUrl = url.toLowerCase();
    return (
      videoExtensions.some((ext) => lowerUrl.includes(ext)) ||
      lowerUrl.includes('/video/') ||
      lowerUrl.includes('video=true') ||
      lowerUrl.match(/\.(mp4|mov|avi|webm|mkv|flv|wmv|m4v|mpg|mpeg|3gp)(\?|$)/i) !== null
    );
  };

  // Helper function to get media filters based on post type
  const getMediaFiltersForPostType = (postType: PostType | ''): MediaType[] => {
    switch (postType) {
      case 'photo':
        return ['IMAGE', 'PHOTO', 'GALLERY'];
      case 'video':
      case 'reel':
      case 'story':
        return ['VIDEO'];
      case 'link':
        return ['BOOKMARK']; // Show only bookmarks for link posts
      case 'text':
      default:
        return ['IMAGE', 'VIDEO', 'GALLERY', 'PHOTO', 'BOOKMARK']; // Show all for text or no post type
    }
  };

  // Function to open media library with appropriate filters
  const handleOpenMediaLibrary = () => {
    const filters = getMediaFiltersForPostType(formData.postType);
    setMediaFilters(filters);
    setShowMediaLibrary(true);
  };

  useEffect(() => {
    if (previousEditMode.current !== editMode) {
      hasInitializedProfiles.current = false;
      previousEditMode.current = editMode;
    }

    if (editMode && selectedPost?.socialProfileId) {
      if (!hasInitializedProfiles.current) {
        setSelectedFormProfiles(new Set([selectedPost.socialProfileId]));
        hasInitializedProfiles.current = true;
      }
    } else if (!editMode && selectedProfiles.size > 0) {
      if (!hasInitializedProfiles.current) {
        setSelectedFormProfiles(new Set(selectedProfiles));
        hasInitializedProfiles.current = true;
      }
    }
  }, [editMode, selectedProfiles, selectedPost, setSelectedFormProfiles]);

  // Auto-detect post type when media or link changes (but only if postType is not already set)
  // This prevents overriding explicitly set post types (e.g., from topic selection)
  useEffect(() => {
    // Skip auto-detection if postType is already set (was explicitly chosen)
    if (formData.postType) {
      return;
    }

    const detectedType = detectPostType(formData.mediaUrl, formData.linkUrl, formData.message);
    if (detectedType) {
      setFormData((prev) => ({ ...prev, postType: detectedType }));
    } else if (!formData.mediaUrl && !formData.linkUrl && formData.message) {
      // If only message is present, default to text/thought
      setFormData((prev) => ({ ...prev, postType: 'text' }));
    }
  }, [formData.mediaUrl, formData.linkUrl, formData.message, formData.postType, setFormData]);

  const refetchQueriesConfig = useMemo(() => {
    if (selectedProfiles.size === 0) {
      return [];
    }
    return [
      {
        query: GET_POSTS,
        variables: { socialProfileIds: Array.from(selectedProfiles) },
      },
    ];
  }, [selectedProfiles]);

  const [createPost, { loading: createLoading, error: createError }] = useMutation(
    CREATE_POST_MUTATION,
    {
      onCompleted: () => {
        setFormData({
          message: '',
          postType: '',
          mediaUrl: '',
          linkUrl: '',
          scheduleTime: new Date(),
          isStory: false,
          notes: '',
          isDraft: false,
        });
        if (onClose) onClose();
      },
      refetchQueries: refetchQueriesConfig,
    }
  );

  const [updatePost, { loading: updateLoading, error: updateError }] = useMutation(
    UPDATE_POST_MUTATION,
    {
      onCompleted: () => {
        setEditMode(false);
        setSelectedPost(null);
        setFormData({
          message: '',
          postType: '',
          mediaUrl: '',
          linkUrl: '',
          scheduleTime: new Date(),
          isStory: false,
          notes: '',
          isDraft: false,
        });
        if (onClose) onClose();
      },
      refetchQueries: refetchQueriesConfig,
    }
  );

  const [
    adaptContent,
    { loading: adaptContentLoading, data: adaptContentData /*, error: adaptContentError */ },
  ] = useLazyQuery(ADAPT_CONTENT_FOR_PLATFORMS, {
    fetchPolicy: 'network-only', // Always fetch fresh data, don't use cache
    onCompleted: () => {
      setShowContentAdaptationModal(true);
      setContentAdaptationError(null);
    },
    onError: (error) => {
      setContentAdaptationError(error.message);
      setShowContentAdaptationModal(true);
    },
  });

  const handleRetryAdaptContent = () => {
    setContentAdaptationError(null);
    // Call adaptContent again with network-only to force a fresh request
    adaptContent({
      variables: { content: formData.message },
      fetchPolicy: 'network-only',
    });
  };

  const activeProfiles = useMemo(() => {
    const profiles =
      socialProfilesData?.socialProfiles?.filter((profile) => profile.isActive) || [];
    return [...profiles].sort((a, b) => {
      const platformCompare = (a.platform || '').localeCompare(b.platform || '');
      if (platformCompare !== 0) {
        return platformCompare;
      }
      return (a.profileName || '').localeCompare(b.profileName || '');
    });
  }, [socialProfilesData]);

  const actionProfiles = useMemo(() => {
    if (editMode && selectedPost?.socialProfileId) {
      const profile = activeProfiles.find((p) => p.id === selectedPost.socialProfileId);
      return profile ? [profile] : [];
    } else {
      return activeProfiles.filter((profile) => selectedProfiles.has(profile.id));
    }
  }, [editMode, selectedPost, activeProfiles, selectedProfiles]);

  useEffect(() => {
    if (actionProfiles.length > 0) {
      setSelectedFormProfiles((prev) => {
        const newSet = new Set(prev);
        actionProfiles.forEach((profile) => {
          newSet.add(profile.id);
        });
        return newSet;
      });
    }
  }, [actionProfiles, setSelectedFormProfiles]);

  const handleFormProfileToggle = (profileId: string) => {
    setSelectedFormProfiles((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(profileId)) {
        newSelected.delete(profileId);
      } else {
        newSelected.add(profileId);
      }
      return newSelected;
    });
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    // Check if it's a topic from media library
    const topicData = e.dataTransfer.getData('application/json');
    if (topicData) {
      try {
        const topic: Topic = JSON.parse(topicData);
        setFormData((prev) => ({
          ...prev,
          mediaUrl: topic.mediaUrl || '',
          linkUrl: topic.destinationUrl || '',
          message: prev.message || topic.summary || '',
          postType:
            topic.mediaType === 'VIDEO'
              ? 'video'
              : topic.mediaType === 'BOOKMARK'
                ? 'link'
                : topic.mediaType === 'IMAGE' || topic.mediaType === 'PHOTO'
                  ? 'photo'
                  : prev.postType,
        }));
        return;
      } catch (error) {
        console.error('Error parsing topic data:', error);
      }
    }

    // Handle file drop
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await handleFileUpload(files[0]);
    }
  };

  const handleFileUpload = async (file: File) => {
    const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const allowedVideoTypes = [
      'video/mp4',
      'video/mpeg',
      'video/quicktime',
      'video/x-msvideo',
      'video/webm',
    ];

    const isImage = allowedImageTypes.includes(file.type);
    const isVideo = allowedVideoTypes.includes(file.type);

    if (!isImage && !isVideo) {
      alert('Please upload an image or video file');
      return;
    }

    setUploading(true);

    if (isVideo) {
      // Videos go straight to video hosting (resumable), then the post uses the
      // MP4 that social networks accept once it's ready.
      try {
        if (!accountId) throw new Error('Choose a site first');
        setUploadNote('Uploading 0%');
        const { assetId } = await uploadVideo(apollo, accountId, file, (p) =>
          setUploadNote(`Uploading ${p}%`)
        );
        setUploadNote('Preparing video…');
        const video = await waitForVideo(apollo, accountId, assetId, { needMp4: true });
        setFormData((prev) => ({ ...prev, mediaUrl: video.mp4Url || '', postType: 'video' }));
      } catch (error) {
        console.error('Error uploading video:', error);
        alert(error instanceof Error ? error.message : 'Failed to upload the video.');
      } finally {
        setUploading(false);
        setUploadNote(null);
      }
      return;
    }

    try {
      const formDataObj = new FormData();
      // Stored under this site's folder (and only if you're on it).
      if (accountId) formDataObj.append('accountId', accountId);
      formDataObj.append('file', file);

      const token = localStorage.getItem('token');
      const backendHost = process.env.REACT_APP_BACKEND_HOST || 'http://localhost:4000';

      const response = await fetch(`${backendHost}/api/upload/media`, {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formDataObj,
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body?.error || 'Upload failed');
      }

      const result = await response.json();
      if (result.url) {
        setFormData((prev) => ({
          ...prev,
          mediaUrl: result.url,
          postType: isVideo ? 'video' : 'photo',
        }));
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert(
        error instanceof Error && error.message !== 'Upload failed'
          ? error.message
          : 'Failed to upload file. Please try again.'
      );
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleLinkChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const currentPostType = formData.postType || 'text';

    // For photo, video, and reel posts with existing media, just store the link without fetching metadata
    if (
      (currentPostType === 'photo' || currentPostType === 'video' || currentPostType === 'reel') &&
      formData.mediaUrl
    ) {
      setFormData((prev) => ({ ...prev, linkUrl: value }));
      return;
    }

    setFormData((prev) => ({ ...prev, linkUrl: value }));

    if (value) {
      try {
        const urlObj = new URL(value);
        if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
          setMetadataError('Invalid URL');
          return;
        }

        // For photo, video, and reel posts with existing media, don't fetch metadata
        if (
          (currentPostType === 'photo' ||
            currentPostType === 'video' ||
            currentPostType === 'reel') &&
          formData.mediaUrl
        ) {
          return;
        }

        setMetadataLoading(true);
        setMetadataError(null);

        const backendHost = process.env.REACT_APP_BACKEND_HOST || 'http://localhost:4000';
        const response = await fetch(
          `${backendHost}/api/utilities/metadata?url=${encodeURIComponent(value)}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        // Handle 403 errors gracefully - allow user to continue without preview
        if (response.status === 403) {
          // For bookmark posts, 403 is not an error - just continue without metadata
          setFormData((prev) => ({
            ...prev,
            linkUrl: value,
            postType: prev.postType || 'link',
          }));
          setMetadataLoading(false);
          setMetadataError(null);
          return;
        }

        if (!response.ok) {
          throw new Error('Failed to fetch metadata');
        }

        const result: MetadataResponse = await response.json();

        if (result.success && result.data) {
          setFormData((prev) => ({
            ...prev,
            linkUrl: value,
            // Don't override mediaUrl if it already exists (for photo posts)
            mediaUrl: prev.mediaUrl || result.data?.image || '',
            message: prev.message || result.data?.summary || '',
            postType: prev.postType || 'link',
          }));
        }
      } catch (error) {
        console.error('Error fetching metadata:', error);
        // Only show error if it's not a 403 (403 is handled above)
        setMetadataError('Failed to fetch link preview');
      } finally {
        setMetadataLoading(false);
      }
    }
  };

  const handleTopicSelect = (topic: Topic) => {
    // Determine post type based on topic's mediaType
    // But preserve special post types (reel, story) if they're already selected
    setFormData((prev) => {
      const currentPostType = prev.postType;
      const isReel = currentPostType === 'reel';
      const isStory = currentPostType === 'story';
      let postType: PostType | '' = '';

      if (topic.mediaType === 'VIDEO') {
        // If user selected 'reel' or 'story', keep it; otherwise use 'video'
        postType = isReel ? 'reel' : isStory ? 'story' : 'video';
      } else if (topic.mediaType === 'BOOKMARK') {
        postType = 'link';
      } else if (topic.mediaType === 'IMAGE' || topic.mediaType === 'PHOTO') {
        // If user selected 'story', keep it; otherwise use 'photo'
        postType = isStory ? 'story' : 'photo';
      } else if (topic.mediaType === 'GALLERY') {
        // Gallery is treated as photo, but preserve story if selected
        postType = isStory ? 'story' : 'photo';
      } else {
        // Default to link if we have a destinationUrl, otherwise preserve story or use photo if we have mediaUrl
        if (topic.destinationUrl) {
          postType = 'link';
        } else if (topic.mediaUrl) {
          postType = isStory ? 'story' : 'photo';
        } else {
          postType = '';
        }
      }

      return {
        ...prev,
        mediaUrl: topic.mediaUrl || '',
        linkUrl: topic.destinationUrl || '',
        message: prev.message || topic.summary || '',
        postType: postType || prev.postType,
      };
    });
    setShowMediaLibrary(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.message.trim()) {
      alert('Please enter a message');
      return;
    }

    try {
      if (editMode) {
        let scheduleTimestamp = formData.scheduleTime;
        if (scheduleTimestamp instanceof Date) {
          scheduleTimestamp = Math.floor(scheduleTimestamp.getTime());
        } else if (typeof scheduleTimestamp === 'number') {
          scheduleTimestamp = Math.floor(scheduleTimestamp);
        } else {
          scheduleTimestamp = Math.floor(new Date(scheduleTimestamp).getTime());
        }

        // Get the platform from the post's social profile to keep it consistent
        const postProfile = selectedPost?.socialProfileId
          ? activeProfiles.find((p) => p.id === selectedPost.socialProfileId)
          : null;
        const postPlatform = postProfile?.platform || selectedPost?.platform || selectedPlatforms;

        const submitData = {
          id: selectedPost?.id,
          title: formData.postType === 'text' ? formData.message : formData.message,
          summary: formData.message,
          postUrl: formData.linkUrl || '',
          imageUrl: formData.mediaUrl || '',
          thoughts: formData.postType === 'text' ? formData.message : '',
          postType: formData.isStory ? 'story' : formData.postType || 'text',
          platform: postPlatform,
          socialProfileId: selectedPost?.socialProfileId,
          scheduleTime: scheduleTimestamp.toString(),
          notes: formData.notes || '',
          isDraft: formData.isDraft || false,
        };

        updatePost({
          variables: {
            id: selectedPost?.id,
            input: submitData,
          },
        });
      } else {
        const availableProfiles = activeProfiles.filter((profile) =>
          selectedProfiles.has(profile.id)
        );

        let selectedProfileIds: string[];
        if (availableProfiles.length === 1) {
          selectedProfileIds = [availableProfiles[0].id];
        } else {
          selectedProfileIds = Array.from(selectedFormProfiles);
          if (selectedProfileIds.length === 0) {
            alert('Please select at least one profile to post to');
            return;
          }
        }

        let scheduleTimestamp = formData.scheduleTime;
        if (scheduleTimestamp instanceof Date) {
          scheduleTimestamp = Math.floor(scheduleTimestamp.getTime());
        } else if (typeof scheduleTimestamp === 'number') {
          scheduleTimestamp = Math.floor(scheduleTimestamp);
        } else {
          scheduleTimestamp = Math.floor(new Date(scheduleTimestamp).getTime());
        }

        const submitData = {
          title: formData.postType === 'text' ? formData.message : formData.message,
          summary: formData.message,
          postUrl: formData.linkUrl || '',
          imageUrl: formData.mediaUrl || '',
          thoughts: formData.postType === 'text' ? formData.message : '',
          postType: formData.isStory ? 'story' : formData.postType || 'text',
          platform: selectedPlatforms,
          scheduleTime: scheduleTimestamp.toString(),
          notes: formData.notes || '',
          isDraft: formData.isDraft || false,
        };

        const creationPromises = selectedProfileIds.map((profileId) =>
          createPost({
            variables: {
              input: {
                ...submitData,
                socialProfileId: profileId,
                accountId: selectedAccount?.id || '',
              },
            },
          })
        );

        await Promise.all(creationPromises);
      }
    } catch (error) {
      console.error('Error creating posts:', error);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setSelectedPost(null);
    setFormData({
      message: '',
      postType: '',
      mediaUrl: '',
      linkUrl: '',
      scheduleTime: new Date(),
      isStory: false,
      notes: '',
      isDraft: false,
    });
    setSelectedFormProfiles(new Set());
    if (onClose) onClose();
  };

  const scheduleTimeValue = useMemo(() => {
    if (!formData.scheduleTime) {
      return '';
    }

    try {
      let date: Date;

      if (formData.scheduleTime instanceof Date) {
        date = formData.scheduleTime;
      } else if (typeof formData.scheduleTime === 'number') {
        // Handle both seconds and milliseconds timestamps
        const timestamp =
          formData.scheduleTime.toString().length <= 10
            ? formData.scheduleTime * 1000
            : formData.scheduleTime;
        date = new Date(timestamp);
      } else {
        date = new Date(formData.scheduleTime);
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return '';
      }

      return format(date, "yyyy-MM-dd'T'HH:mm");
    } catch (error) {
      console.error('Error formatting schedule time:', error);
      return '';
    }
  }, [formData.scheduleTime]);

  const isPostPublished = editMode && selectedPost?.socialPublishDate ? true : false;
  const isPostDeleted = editMode && selectedPost?.deletionStatus === 'deleted' ? true : false;
  const canEdit = !isPostPublished && !isPostDeleted;

  // Create a preview post object from form data
  const getPreviewPost = (): SocialPost | null => {
    // Determine platform for preview
    let previewPlatform: Platform | '' = '';
    let previewProfileName = 'Profile';

    if (editMode && selectedPost) {
      previewPlatform = (selectedPost.platform as Platform) || selectedPlatforms;
      previewProfileName = selectedPost.socialProfile?.profileName || 'Profile';
    } else {
      // Get platform from first selected profile
      const firstSelectedProfile = activeProfiles.find((profile) =>
        selectedFormProfiles.has(profile.id)
      );
      if (firstSelectedProfile) {
        previewPlatform = (firstSelectedProfile.platform as Platform) || selectedPlatforms;
        previewProfileName = firstSelectedProfile.profileName || 'Profile';
      } else {
        previewPlatform = selectedPlatforms;
      }
    }

    if (!previewPlatform) {
      return null;
    }

    // Convert SimplifiedPostFormData to SocialPost format
    const previewTitle =
      formData.postType === 'text'
        ? formData.message
        : formData.postType === 'photo' ||
            formData.postType === 'video' ||
            formData.postType === 'reel' ||
            formData.postType === 'story'
          ? formData.message
          : formData.message;

    return {
      id: selectedPost?.id || 'preview',
      title: previewTitle,
      summary: formData.message,
      postUrl: formData.linkUrl,
      imageUrl: formData.mediaUrl,
      thoughts: formData.postType === 'text' ? formData.message : '',
      scheduleTime:
        formData.scheduleTime instanceof Date
          ? formData.scheduleTime.getTime()
          : typeof formData.scheduleTime === 'number'
            ? formData.scheduleTime
            : new Date().getTime(),
      platform: previewPlatform as Platform,
      socialProfileId: selectedPost?.socialProfileId || '',
      postType: formData.isStory ? 'story' : formData.postType || 'text',
      socialProfile: {
        profileName: previewProfileName,
      },
      isDraft: formData.isDraft || false,
    };
  };

  // Get all available post types (for tabs)
  const allPostTypes: PostType[] = ['text', 'link', 'photo', 'video', 'reel', 'story'];

  // Get available platforms for the selected post type
  const availablePlatformsForPostType = useMemo(() => {
    if (!formData.postType) {
      return activeProfiles;
    }

    const postType = formData.postType as PostType;
    return activeProfiles.filter((profile) => {
      const platform = profile.platform?.toLowerCase() || '';
      const capabilities = PLATFORM_CAPABILITIES[platform] || [];
      return (
        capabilities.includes(postType) ||
        (capabilities.includes('photo') && (postType === 'image' || postType === 'photo'))
      );
    });
  }, [formData.postType, activeProfiles]);

  // Filter selected profiles to only include those that support the current post type
  useEffect(() => {
    if (formData.postType && selectedFormProfiles.size > 0) {
      const supportedProfileIds = new Set(availablePlatformsForPostType.map((p) => p.id));

      setSelectedFormProfiles((prev) => {
        const filtered = new Set<string>();
        prev.forEach((id) => {
          if (supportedProfileIds.has(id)) {
            filtered.add(id);
          }
        });
        return filtered;
      });
    }
  }, [
    formData.postType,
    availablePlatformsForPostType,
    setSelectedFormProfiles,
    selectedFormProfiles.size,
  ]);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-white">
          {editMode ? 'Edit Post' : 'Create New Post'}
        </h2>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowPreview(true)}
              disabled={!getPreviewPost() || !formData.message.trim()}
              className="h-6 bg-gray-600 text-white hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              title={
                !getPreviewPost()
                  ? 'Please select a platform and add content to preview'
                  : 'Preview how your post will look'
              }
            >
              <Eye className="w-4 h-4" />
              Preview
            </Button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="text-cmp-ink-2 hover:text-white transition-colors p-1"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>
      </div>

      {!activeProfiles || activeProfiles.length === 0 ? (
        <div className="p-4 bg-warning/20 border border-warning rounded text-warning">
          You do not have any active social profiles set up. Please contact your administrator to
          add social profiles to your account.
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Post Type Tabs */}
          <Tabs
            value={formData.postType || 'text'}
            onValueChange={(value) => {
              setFormData((prev) => ({
                ...prev,
                postType: value as PostType,
                isStory: value === 'story',
              }));
            }}
          >
            <TabsList className="w-full bg-form-dark border border-cmp-line-2 p-1">
              {allPostTypes.map((type) => {
                const icons: Record<string, React.ReactNode> = {
                  text: <Type className="w-4 h-4" />,
                  link: <LinkIcon className="w-4 h-4" />,
                  photo: <ImageIcon className="w-4 h-4" />,
                  video: <Video className="w-4 h-4" />,
                  reel: <Video className="w-4 h-4" />,
                  story: <ImageIcon className="w-4 h-4" />,
                };
                const labels: Record<string, string> = {
                  text: 'Thought',
                  link: 'Link',
                  photo: 'Photo',
                  video: 'Video',
                  reel: 'Reel',
                  story: 'Story',
                };
                return (
                  <TabsTrigger
                    key={type}
                    value={type}
                    className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-white"
                  >
                    {icons[type]}
                    <span className="ml-2">{labels[type]}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            <TabsContent value={formData.postType || 'text'} className="mt-4">
              {/* Determine the effective post type (treat empty as 'text') */}
              {(() => {
                const effectivePostType = formData.postType || 'text';
                return effectivePostType === 'link' ? (
                  <>
                    {/* Link Input - Required for Bookmark */}
                    <div>
                      <Label htmlFor="linkUrl" className="text-white mb-2">
                        Paste a link or select from bookmarks *
                      </Label>
                      <div className="flex gap-2 mb-2">
                        <Input
                          id="linkUrl"
                          type="url"
                          value={formData.linkUrl}
                          onChange={handleLinkChange}
                          placeholder="https://example.com/article"
                          disabled={metadataLoading}
                          className="bg-form-dark text-cmp-ink flex-1"
                          required
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleOpenMediaLibrary}
                          className="bg-form-dark text-cmp-ink whitespace-nowrap"
                        >
                          <LinkIcon className="w-4 h-4 mr-2" />
                          Select Bookmark
                        </Button>
                      </div>
                      {metadataLoading && (
                        <p className="mt-1 text-sm text-cmp-ink-2">Fetching link preview...</p>
                      )}
                      {metadataError && metadataError !== '403' && (
                        <p className="mt-1 text-sm text-error">Error: {metadataError}</p>
                      )}
                      {formData.linkUrl && !formData.mediaUrl && !metadataLoading && (
                        <p className="mt-1 text-sm text-cmp-ink-2">
                          Link preview unavailable. You can still post with just the link, message,
                          schedule, and profiles.
                        </p>
                      )}
                    </div>

                    {/* Derived Image Preview - Show after link is pasted */}
                    {formData.linkUrl && formData.mediaUrl && (
                      <div className="space-y-2">
                        <Label className="text-white">Link Preview Image</Label>
                        <div className="relative border border-cmp-line-2 rounded-lg overflow-hidden bg-form-dark">
                          <img
                            src={formData.mediaUrl}
                            alt="Link preview"
                            className="w-full max-h-64 object-contain"
                          />
                          <button
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, mediaUrl: '' }))}
                            className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1"
                            title="Remove preview image"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-sm text-cmp-ink-2">
                          This image was automatically extracted from the link
                        </p>
                      </div>
                    )}

                    {/* Option to Override Image */}
                    {formData.linkUrl && (
                      <div className="space-y-2">
                        <Label className="text-white">Override Image (Optional)</Label>
                        <div
                          ref={dropZoneRef}
                          onDragEnter={handleDragEnter}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                          className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                            isDragging
                              ? 'border-primary bg-primary/10'
                              : 'border-cmp-line-2 bg-form-dark hover:border-cmp-line-2'
                          }`}
                        >
                          {formData.mediaUrl && formData.linkUrl ? (
                            <div className="space-y-2">
                              <p className="text-sm text-cmp-ink-2">
                                Upload a new image to replace the preview
                              </p>
                              <div className="flex gap-2 justify-center">
                                <input
                                  ref={fileInputRef}
                                  type="file"
                                  accept="image/*"
                                  onChange={handleFileInputChange}
                                  className="hidden"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => fileInputRef.current?.click()}
                                  disabled={uploading}
                                  className="bg-form-dark text-cmp-ink"
                                >
                                  {uploading ? (
                                    <>
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                      {uploadNote || 'Uploading...'}
                                    </>
                                  ) : (
                                    <>
                                      <ImagePlus className="w-4 h-4 mr-2" />
                                      Upload New Image
                                    </>
                                  )}
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={handleOpenMediaLibrary}
                                  className="bg-form-dark text-cmp-ink"
                                >
                                  <ImageIcon className="w-4 h-4 mr-2" />
                                  From Media Library
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <p className="text-sm text-cmp-ink-2">
                                Upload an image to use with this bookmark
                              </p>
                              <div className="flex gap-2 justify-center">
                                <input
                                  ref={fileInputRef}
                                  type="file"
                                  accept="image/*"
                                  onChange={handleFileInputChange}
                                  className="hidden"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => fileInputRef.current?.click()}
                                  disabled={uploading}
                                  className="bg-form-dark text-cmp-ink"
                                >
                                  {uploading ? (
                                    <>
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                      {uploadNote || 'Uploading...'}
                                    </>
                                  ) : (
                                    <>
                                      <ImagePlus className="w-4 h-4 mr-2" />
                                      Upload Image
                                    </>
                                  )}
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={handleOpenMediaLibrary}
                                  className="bg-form-dark text-cmp-ink"
                                >
                                  <ImageIcon className="w-4 h-4 mr-2" />
                                  Media Library
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Drag and Drop Zone - For other post types (not text, not link) */}
                    {effectivePostType !== 'text' && (
                      <div
                        ref={dropZoneRef}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={(e) => {
                          if (
                            !formData.mediaUrl &&
                            !uploading &&
                            fileInputRef.current &&
                            !(e.target as HTMLElement).closest('button')
                          ) {
                            fileInputRef.current.click();
                          }
                        }}
                        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                          isDragging
                            ? 'border-primary bg-primary/10'
                            : 'border-cmp-line-2 bg-form-dark hover:border-cmp-line-2 mb-4'
                        } ${!formData.mediaUrl && !uploading ? 'cursor-pointer' : ''}`}
                      >
                        {formData.mediaUrl ? (
                          <div className="relative">
                            {formData.postType === 'video' ||
                            formData.postType === 'reel' ||
                            (formData.postType === 'story' && isVideoUrl(formData.mediaUrl)) ? (
                              <video
                                src={formData.mediaUrl}
                                controls
                                className="w-full max-h-64 object-contain rounded"
                                onError={(e) => {
                                  console.error('Video failed to load:', formData.mediaUrl);
                                  // Fallback to image display if video fails
                                  const target = e.target as HTMLVideoElement;
                                  target.style.display = 'none';
                                  const img = document.createElement('img');
                                  img.src = formData.mediaUrl;
                                  img.alt = 'Post media';
                                  img.className = 'w-full max-h-64 object-contain rounded';
                                  target.parentNode?.appendChild(img);
                                }}
                              />
                            ) : (
                              <img
                                src={formData.mediaUrl}
                                alt="Post media"
                                className="w-full max-h-64 object-contain rounded"
                                onError={(e) => {
                                  console.error('Image failed to load:', formData.mediaUrl);
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            )}
                            <button
                              type="button"
                              onClick={() => setFormData((prev) => ({ ...prev, mediaUrl: '' }))}
                              className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div className="flex flex-col items-center gap-2">
                              <Upload className="w-12 h-12 text-cmp-ink-2" />
                              <p className="text-white">
                                Drag and drop a photo, video, or link here, or click to browse
                              </p>
                            </div>
                            <div className="flex gap-2 justify-center">
                              <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*,video/*"
                                onChange={handleFileInputChange}
                                className="hidden"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                className="bg-form-dark text-cmp-ink"
                              >
                                {uploading ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    {uploadNote || 'Uploading...'}
                                  </>
                                ) : (
                                  <>
                                    <ImagePlus className="w-4 h-4 mr-2" />
                                    Upload Media
                                  </>
                                )}
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={handleOpenMediaLibrary}
                                className="bg-form-dark text-cmp-ink"
                              >
                                <ImageIcon className="w-4 h-4 mr-2" />
                                Media Library
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Message Input */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label
                    htmlFor="message"
                    className="font-cmp-mono text-[0.66rem] tracking-[0.14em] uppercase text-cmp-ink-3"
                  >
                    Message *
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (!formData.message.trim()) {
                        alert('Please enter a message first');
                        return;
                      }
                      adaptContent({ variables: { content: formData.message } });
                    }}
                    disabled={adaptContentLoading || !formData.message.trim()}
                    className="bg-form-dark text-cmp-ink hover:bg-form-dark/80 h-6 text-xs"
                  >
                    {adaptContentLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Thinking...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Try AI Suggestions
                      </>
                    )}
                  </Button>
                </div>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                  placeholder="What's on your mind?"
                  rows={4}
                  required
                  className="bg-form-dark text-cmp-ink mb-1"
                />
                <p className="mb-2 text-sm text-cmp-ink-2">{formData.message.length} characters</p>
              </div>

              {/* Link Input - For photo and video posts only (show below message) */}
              {((formData.postType || 'text') === 'photo' ||
                (formData.postType || 'text') === 'video') && (
                <div>
                  <Label htmlFor="linkUrl" className="text-white mt-4 mb-2">
                    Share a link (Optional)
                  </Label>
                  <Input
                    id="linkUrl"
                    type="url"
                    value={formData.linkUrl}
                    onChange={handleLinkChange}
                    placeholder="https://example.com/article"
                    className="bg-form-dark text-cmp-ink"
                  />
                  <p className="mt-1 text-sm text-cmp-ink-2">
                    {formData.postType === 'photo'
                      ? 'Add a link to share with your photo.'
                      : 'Add a link to share with your video.'}
                  </p>
                </div>
              )}

              {/* Show content based on post type - Hidden for Thought (text) and Bookmark (link) posts */}
            </TabsContent>
          </Tabs>

          {/* Schedule Time */}
          <div>
            <Label
              htmlFor="scheduleTime"
              className="font-cmp-mono text-[0.66rem] tracking-[0.14em] uppercase text-cmp-ink-3 mb-2 block"
            >
              Schedule Time
            </Label>
            <Input
              id="scheduleTime"
              type="datetime-local"
              value={scheduleTimeValue}
              onChange={(e) => {
                const value = e.target.value;

                // Don't update state if value is empty - let user continue typing
                if (!value) {
                  return;
                }

                // Check if the datetime-local value is in a complete format (YYYY-MM-DDTHH:MM)
                // This regex ensures we have a complete datetime-local format
                const datetimeLocalPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

                // Only update state if we have a complete, valid format
                if (datetimeLocalPattern.test(value)) {
                  try {
                    const date = new Date(value);
                    // Check if date is valid
                    if (!isNaN(date.getTime())) {
                      setFormData((prev) => ({ ...prev, scheduleTime: date }));
                    }
                  } catch (error) {
                    console.error('Error parsing schedule time:', error);
                  }
                }
                // If the format is incomplete (user is still typing), don't update state
                // This prevents crashes from invalid intermediate values
              }}
              disabled={isPostPublished || isPostDeleted}
              className="bg-form-dark text-cmp-ink [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer"
            />
          </div>

          {/* Profile Selection - Filtered by post type */}
          {availablePlatformsForPostType.length > 0 && (
            <div>
              <Label className="font-cmp-mono text-[0.66rem] tracking-[0.14em] uppercase text-cmp-ink-3 block pt-2">
                Select profiles to post to:
                {formData.postType &&
                  availablePlatformsForPostType.length < activeProfiles.length && (
                    <span className="text-cmp-ink-2 text-sm ml-2">
                      ({availablePlatformsForPostType.length} of {activeProfiles.length} profiles
                      support {formData.postType})
                    </span>
                  )}
              </Label>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {availablePlatformsForPostType.map((profile) => (
                  <label
                    key={profile.id}
                    className="flex items-center gap-2 cursor-pointer hover:bg-form-dark p-2 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={selectedFormProfiles.has(profile.id)}
                      onChange={() => handleFormProfileToggle(profile.id)}
                      disabled={isPostPublished || isPostDeleted}
                      className="w-4 h-4 accent-cmp-bronze bg-form-dark border-cmp-line rounded focus:ring-primary focus:ring-2"
                    />
                    <div className="flex items-center gap-2">
                      <PlatformIcon platform={profile.platform} size={20} />
                      <span className="text-sm text-white">{profile.profileName}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Nothing can publish this post type — say so, rather than hiding the
              profile picker with no explanation. */}
          {formData.postType && availablePlatformsForPostType.length === 0 && (
            <p className="text-warning text-sm mt-2">
              No profiles support {formData.postType} posts. Please select a different post type or
              add profiles that support it.
            </p>
          )}

          {/* Error Display */}
          {(createError || updateError) && (
            <div className="p-4 bg-error/20 border border-error rounded text-error">
              {(createError as ApolloError)?.message || (updateError as ApolloError)?.message}
            </div>
          )}

          {/* Action Buttons */}
          {canEdit && (
            <div className="flex items-center gap-4 justify-end">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createLoading || updateLoading || !formData.message.trim()}
              >
                {createLoading || updateLoading
                  ? 'Saving...'
                  : editMode
                    ? 'Save Changes'
                    : 'Create Post'}
              </Button>
            </div>
          )}
        </form>
      )}

      {/* Media Library Dialog */}
      {showMediaLibrary && accountId && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-cmp-raise rounded-cmp3 shadow-cmp-e3 border border-cmp-line max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-white">Select from Media Library</h2>
                <button
                  type="button"
                  onClick={() => setShowMediaLibrary(false)}
                  className="text-cmp-ink-2 hover:text-white transition-colors p-1"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="mt-4">
                <TopicsSection
                  accountId={accountId}
                  onTopicSelect={handleTopicSelect}
                  mediaFilters={mediaFilters}
                  onMediaFilterChange={(event, newFilters) => {
                    setMediaFilters(newFilters);
                  }}
                  selectedAccount={selectedAccount}
                  onRefetchReady={() => {}}
                  clickBehavior="select"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content Adaptation Modal */}
      {showContentAdaptationModal && (
        <style>{`
          [data-slot="dialog-overlay"]:last-of-type {
            z-index: 10001 !important;
          }
          [data-slot="dialog-content"]:last-of-type {
            z-index: 10002 !important;
          }
        `}</style>
      )}
      <Dialog open={showContentAdaptationModal} onOpenChange={setShowContentAdaptationModal}>
        <DialogContent className="bg-core-dark border-cmp-line-2 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">AI Suggestions</DialogTitle>
            <DialogDescription className="text-cmp-ink-2">
              Choose an AI suggestion or keep your original message
            </DialogDescription>
          </DialogHeader>

          {contentAdaptationError ? (
            <div className="space-y-4">
              <div className="p-4 bg-error/20 border border-error rounded text-error">
                {contentAdaptationError}
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowContentAdaptationModal(false)}
                  className="bg-form-dark text-cmp-ink"
                >
                  Close
                </Button>
                <Button
                  type="button"
                  onClick={handleRetryAdaptContent}
                  disabled={adaptContentLoading}
                >
                  {adaptContentLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Retrying...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Try Again
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          ) : adaptContentLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <span className="ml-3 text-white">Thinking...</span>
            </div>
          ) : adaptContentData?.adaptContentForPlatforms?.platforms ? (
            <div className="space-y-4">
              {/* Original Message */}
              <div className="p-4 bg-form-dark rounded-lg border border-cmp-line-2">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-white font-semibold">Original Message</Label>
                  <span className="text-sm text-cmp-ink-2">
                    {formData.message.length} characters
                  </span>
                </div>
                <p className="text-cmp-ink whitespace-pre-wrap">{formData.message}</p>
              </div>

              {/* Platform Suggestions */}
              <div className="space-y-3">
                <Label className="text-white font-semibold">Platform-Specific Versions</Label>
                {adaptContentData.adaptContentForPlatforms.platforms.map((platformContent: any) => (
                  <div
                    key={platformContent.platform}
                    className="p-4 bg-form-dark rounded-lg border border-cmp-line-2 hover:border-primary transition-colors cursor-pointer"
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, message: platformContent.content }));
                      setShowContentAdaptationModal(false);
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <PlatformIcon platform={platformContent.platform} size={20} />
                        <span className="text-white font-medium capitalize">
                          {platformContent.platform}
                        </span>
                        {platformContent.isTruncated && (
                          <span className="text-xs text-warning">(Truncated)</span>
                        )}
                      </div>
                      <span className="text-sm text-cmp-ink-2">
                        {platformContent.characterCount} characters
                      </span>
                    </div>
                    <p className="text-cmp-ink whitespace-pre-wrap">{platformContent.content}</p>
                  </div>
                ))}
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowContentAdaptationModal(false)}
                  className="bg-form-dark text-cmp-ink"
                >
                  Keep Original
                </Button>
                <Button
                  type="button"
                  onClick={handleRetryAdaptContent}
                  disabled={adaptContentLoading}
                  variant="outline"
                  className="bg-form-dark text-cmp-ink"
                >
                  {adaptContentLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Retrying...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Try Again
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      {showPreview && (
        <style>{`
          [data-slot="dialog-overlay"]:last-of-type {
            z-index: 10003 !important;
          }
          [data-slot="dialog-content"]:last-of-type {
            z-index: 10004 !important;
          }
        `}</style>
      )}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-core-dark text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Post Preview</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center items-start p-4">
            {getPreviewPost() ? (
              <SocialPostPreview post={getPreviewPost()!} />
            ) : (
              <div className="p-4 text-cmp-ink-2">
                Please select a platform and add content to preview your post.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
